import {makeAutoObservable, observable} from 'mobx';

import {Match, MatchInteraction} from '@/domain/models/Match';
import {User} from '@/domain/models/User';
import {ONE_SECOND_MS} from '@/shared/consts/time';
import {Timer} from '@/shared/types/timer';

import {ByMatchStore, type LastViewport, type Message} from './byMatch';
import {MessageRetryPayload} from './types';

const TEXTING_INTERACTING_TIMEOUT = 8 * ONE_SECOND_MS;
const UPLOADING_IMAGE_INTERACTING_TIMEOUT = 12 * ONE_SECOND_MS;

class MessageStore {
  private byMatch = observable.map<Match['id'], ByMatchStore>(undefined, {
    deep: false,
  });
  private draftsByMatch = observable.map<Match['id'], string>(undefined, {
    deep: false,
  });
  private interactions = observable.map<
    Match['id'],
    {
      timeout: Timer;
      payload: MatchInteraction;
    }
  >();
  private retryPayloadsByMessageKey = observable.map<
    string,
    MessageRetryPayload
  >(undefined, {
    deep: false,
  });

  constructor() {
    makeAutoObservable(this);
  }

  private ensureByMatchStore(matchId: Match['id']): ByMatchStore {
    const existing = this.byMatch.get(matchId);
    if (existing) {
      return existing;
    }

    const created = new ByMatchStore(matchId);
    this.byMatch.set(matchId, created);
    return created;
  }

  getMessages(matchId: Match['id']) {
    return this.byMatch.get(matchId)?.messages;
  }

  getReplyTargetMessages(matchId: Match['id']) {
    return this.byMatch.get(matchId)?.replyTargetMessages;
  }

  getDraft(matchId: Match['id']): string {
    return this.draftsByMatch.get(matchId) ?? '';
  }

  setDraft(matchId: Match['id'], value: string) {
    if (value.length === 0) {
      this.draftsByMatch.delete(matchId);
      return;
    }

    this.draftsByMatch.set(matchId, value);
  }

  clearDraft(matchId: Match['id']) {
    this.draftsByMatch.delete(matchId);
  }

  resetChatState() {
    this.byMatch.clear();
    this.interactions.clear();
  }

  resetInteractionState() {
    this.interactions.clear();
  }

  reset() {
    this.resetChatState();
    this.retryPayloadsByMessageKey.clear();
    this.draftsByMatch.clear();
  }

  addMessages(matchId: Match['id'], messages: Message[]) {
    if (messages.length === 0) {
      return 0;
    }

    this.ensureByMatchStore(matchId).addMessages(messages);
  }

  setIsRead(matchId: Match['id'], value: boolean) {
    this.ensureByMatchStore(matchId).isRead = value;
  }

  getIsRead(matchId: Match['id']): boolean {
    return this.byMatch.get(matchId)?.isRead ?? true;
  }

  hasUnread(matchId: Match['id']): boolean {
    return !this.getIsRead(matchId);
  }

  applyLatestMessages(matchId: Match['id'], messages: Message[]) {
    if (messages.length === 0) {
      return;
    }

    this.ensureByMatchStore(matchId).applyLatestMessages(messages);
  }

  refreshMessages() {
    this.byMatch.forEach((store) => {
      store.setShouldRefresh(true);
    });
  }

  getShouldRefreshMessages(matchId: Match['id']) {
    const store = this.byMatch.get(matchId);
    if (!store) {
      return false;
    }

    return store.shouldRefresh;
  }

  setRefreshedMessages(matchId: Match['id']) {
    const store = this.byMatch.get(matchId);
    if (!store) {
      return;
    }

    store.setShouldRefresh(false);
  }

  addReplyTargetMessages(
    matchId: Match['id'],
    messages: Message[] | undefined
  ) {
    if (!messages) {
      return;
    }

    this.ensureByMatchStore(matchId).addReplyTargetMessages(messages);
  }

  getReplyTargetMessage(
    matchId: Match['id'],
    messageId: Message['id']
  ): Message | undefined {
    return this.byMatch.get(matchId)?.getReplyTargetMessage(messageId);
  }

  syncMessage(matchId: Match['id'], message: Message): boolean {
    return this.byMatch.get(matchId)?.syncMessage(message) ?? false;
  }

  syncReplyTargetMessage(matchId: Match['id'], message: Message): boolean {
    return this.byMatch.get(matchId)?.syncReplyTargetMessage(message) ?? false;
  }

  detachMessagesReplyingTo(
    matchId: Match['id'],
    targetMessageId: Message['id']
  ) {
    this.byMatch.get(matchId)?.detachMessagesReplyingTo(targetMessageId);
  }

  getOldestCreatedAt(matchId: Match['id']) {
    return this.byMatch.get(matchId)?.oldestCreatedAt;
  }

  fetchedAllOlder(matchId: Match['id']) {
    return this.byMatch.get(matchId)?.fetchedAllOlder ?? false;
  }

  setFetchedAllOlder(matchId: Match['id'], value: boolean) {
    this.ensureByMatchStore(matchId).setFetchedAllOlder(value);
  }

  getLastMessage(matchId: Match['id']) {
    return this.byMatch.get(matchId)?.getLastMessage();
  }

  getMessage(matchId: Match['id'], messageId: Message['id']) {
    return this.byMatch.get(matchId)?.getMessage(messageId);
  }

  updateMessage(
    matchId: Match['id'],
    messageId: Message['id'],
    updater: (message: Message) => void
  ) {
    this.byMatch.get(matchId)?.updateMessage(messageId, updater);
  }

  removeMessage(matchId: Match['id'], messageId: Message['id']) {
    this.byMatch.get(matchId)?.removeMessage(messageId);
  }

  replaceMessage(
    matchId: Match['id'],
    oldMessageId: Message['id'],
    nextMessage: Message
  ) {
    this.ensureByMatchStore(matchId).replaceMessage(oldMessageId, nextMessage);
  }

  setInteraction(interaction: MatchInteraction) {
    const currentInteraction = this.interactions.get(interaction.match_id);
    if (currentInteraction) {
      clearTimeout(currentInteraction.timeout);
    }

    const timeout = setTimeout(
      () => {
        this.interactions.delete(interaction.match_id);
      },
      interaction.type === 'uploading_image'
        ? UPLOADING_IMAGE_INTERACTING_TIMEOUT
        : TEXTING_INTERACTING_TIMEOUT
    );

    this.interactions.set(interaction.match_id, {
      timeout,
      payload: interaction,
    });
  }

  getInteraction(matchId: Match['id']) {
    return this.interactions.get(matchId)?.payload;
  }

  stopInteraction(matchId: Match['id']) {
    const currentInteraction = this.interactions.get(matchId);
    if (currentInteraction) {
      clearTimeout(currentInteraction.timeout);
    }

    this.interactions.delete(matchId);
  }

  setLastViewport(matchId: Match['id'], value: LastViewport) {
    this.ensureByMatchStore(matchId).setLastViewport(value);
  }

  getLastViewport(matchId: Match['id']) {
    return this.ensureByMatchStore(matchId).getLastViewport();
  }

  clearLastViewport(matchId: Match['id']) {
    this.ensureByMatchStore(matchId).clearLastViewport();
  }

  getCountUnreadFromUser(
    matchId: Match['id'],
    userId: User['id'],
    lastMeReadAt?: number
  ) {
    return this.ensureByMatchStore(matchId).getCountUnreadFromUser(
      userId,
      lastMeReadAt
    );
  }

  private buildRetryPayloadKey(
    matchId: Match['id'],
    messageId: Message['id']
  ): string {
    return `${matchId}:${messageId}`;
  }

  setRetryPayload(
    matchId: Match['id'],
    messageId: Message['id'],
    payload: MessageRetryPayload
  ) {
    this.retryPayloadsByMessageKey.set(
      this.buildRetryPayloadKey(matchId, messageId),
      payload
    );
  }

  getRetryPayload(matchId: Match['id'], messageId: Message['id']) {
    return this.retryPayloadsByMessageKey.get(
      this.buildRetryPayloadKey(matchId, messageId)
    );
  }

  removeRetryPayload(matchId: Match['id'], messageId: Message['id']) {
    this.retryPayloadsByMessageKey.delete(
      this.buildRetryPayloadKey(matchId, messageId)
    );
  }

  hasMessages(matchId: Match['id']) {
    return this.ensureByMatchStore(matchId).hasMessages();
  }
}

export const messageStore = new MessageStore();
